// JobSpecExecutionPanel — admin mirror of installer execution.
// If job_checklist_items has authored custom rows, those DB rows are the source
// of truth and this panel renders them read-only. The installer portal owns
// execution/checking of those rows.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Camera, CheckCircle2, Lock, Clock, AlertTriangle, RotateCcw, ShieldAlert } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useAppData } from "@/data/AppDataContext";
import { supabase } from "@/lib/supabase";
import { createSignedStorageThumbnailUrl } from "@/lib/storageImageUrls";
import { resolveBoundSpec, formatSpecRef, type BoundJobSpec, type JobSpecPhaseName, type RequiredPhotoType, ALL_REQUIRED_PHOTO_TYPES } from "@/data/jobSpec";
import {
  ensureExecution,
  isPhaseUnlocked,
  type JobSpecExecution,
  type PhaseExecution,
} from "@/data/jobSpecExecution";
import { createExecutionRepository } from "@/data/execution/ExecutionRepository";
import { getDownloadUrl } from "@/services/attachment.service";
import { useExecutionProofs } from "@/hooks/useExecutionProofs";
import { requestExecutionProofUploadUrl, uploadProofBytes, type ExecutionProof } from "@/lib/supabase/executions";
import { computeFileHash, isSessionDuplicate, registerUploadHash, findExistingDuplicate } from "@/lib/photoDedup";
import {
  DEFAULT_JOB_CHECKLIST_PHASE_LABELS,
  JOB_CHECKLIST_PHASES,
} from "@/services/job-checklist.service";
import type { JobChecklistPhaseKey } from "@/data/types";

interface Props {
  jobId: string;
  bound?: BoundJobSpec;
}

const INSTALLER_NAME_STORAGE_KEY = "jobspec_execution_installer_name";
const PORTAL_CHECKLIST_PHASE_ORDER: Record<JobChecklistPhaseKey, number> = {
  prep: 1,
  demo: 2,
  rough: 3,
  install: 4,
  finish: 5,
  qa: 6,
  rework: 7,
  custom: 8,
};

type PortalChecklistRow = {
  id: string;
  jobId: string;
  phaseKey: JobChecklistPhaseKey;
  phaseLabel: string;
  itemIndex: number;
  label: string;
  proofLabel: string | null;
  requiresProof: boolean;
  isCustom: boolean;
  checked: boolean;
  checkedBy: string | null;
  checkedAt: string | null;
  sortOrder: number;
};

const EXECUTION_PROOFS_BUCKET = "execution-proofs";

/**
 * Exact caption → RequiredPhotoType mapping built from the portal's CANONICAL
 * proof labels (JobExecutionPage.tsx). Used as a fallback when execution_proofs
 * rows don't yet have the photo_type column populated.
 */
const PORTAL_CAPTION_TO_PHOTO_TYPE: Record<string, RequiredPhotoType> = {
  // Arrival, Prep & Protection
  "Floor Protection Installed":     "prep_floor_protection",
  "Water Shutoff Verification":     "prep_water_shutoff",
  "Workspace & Materials Staged":   "prep_workspace_staged",
  // Demolition & Structural Assessment
  "Existing Wet Area Overview":     "before_existing_tub",
  "Existing Walls & Surround":      "before_walls",
  "Existing Plumbing/P-Trap Area":  "before_plumbing_access",
  "Subfloor & Framing Exposed":     "demo_complete_substrate",
  "Demo Complete / Debris Removed": "demo_debris_removed",
  // Framing, Plumbing & Rough-In
  "Shower Valve Rough-In":          "rough_plumbing_valve",
  "New P-Trap Installed":           "rough_plumbing_drain",
  "Pressure/Leak Test":             "pressure_test",
  // Pan, Substrate & Wall Installation
  "Shower Pan/Basin Set":           "pan_set",
  "Wall Panels Installed":          "wall_panels_installed",
  // Trim, Fixtures & Waterproof Finishing
  "Shower Trim Installed":          "trim_complete",
  "Waterproof Seams & Corners":     "final_caulk_lines",
  "Finish Complete":                "final_full_shower",
  // Final QA, Water Testing & Closeout
  "Water Test Verification":        "qa_water_test_verification",
  "Site Cleaned & Complete":        "qa_site_cleaned_complete",
};

/** Resolve a RequiredPhotoType from a proof row.
 *  Primary:  photo_type column (set by portal after schema migration).
 *  Fallback: exact caption lookup against PORTAL_CAPTION_TO_PHOTO_TYPE.
 */
function resolvePhotoType(
  photoTypeCol: string | null | undefined,
  caption: string | null | undefined,
): RequiredPhotoType | null {
  if (photoTypeCol && ALL_REQUIRED_PHOTO_TYPES.includes(photoTypeCol as RequiredPhotoType)) {
    return photoTypeCol as RequiredPhotoType;
  }
  if (caption) return PORTAL_CAPTION_TO_PHOTO_TYPE[caption] ?? null;
  return null;
}

function formatPhotoLabel(key: string): string {
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function statusBadge(status: PhaseExecution["status"]) {
  switch (status) {
    case "qa_passed":
      return <Badge className="bg-success text-success-foreground gap-1"><CheckCircle2 className="h-3 w-3" /> QA Passed</Badge>;
    case "submitted":
      return <Badge variant="outline" className="gap-1"><Clock className="h-3 w-3" /> Awaiting QA</Badge>;
    case "qa_failed":
      return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" /> QA Failed</Badge>;
    case "in_progress":
      return <Badge variant="secondary">In Progress</Badge>;
    default:
      return <Badge variant="outline">Not Started</Badge>;
  }
}

function getPortalPhaseLabel(phaseKey: JobChecklistPhaseKey, rows: PortalChecklistRow[]): string {
  return rows.find((row) => row.phaseKey === phaseKey)?.phaseLabel || DEFAULT_JOB_CHECKLIST_PHASE_LABELS[phaseKey];
}

function normalizeProofCaption(value: string | null | undefined): string {
  return String(value ?? "").trim().toLowerCase();
}

function proofCaptionForItem(item: PortalChecklistRow): string {
  return String(item.proofLabel || item.label || "").trim();
}

function captionMatchesPhase(caption: string | null | undefined, phaseLabel: string): boolean {
  const cap = normalizeProofCaption(caption);
  const label = normalizeProofCaption(phaseLabel);
  if (!cap || !label) return false;
  return cap === label || cap === normalizeProofCaption(`Additional (${phaseLabel})`) || cap.includes(`(${label})`);
}

function isProofImage(proof: ExecutionProof): boolean {
  return String(proof.mime_type ?? "").startsWith("image/");
}

function getProofUploadKind(file: File) {
  return file.type.startsWith("image/") ? "photo" : "document";
}

function getUploadKey(caption: string, supersedesId?: string | null) {
  return `${normalizeProofCaption(caption)}:${supersedesId ?? "new"}`;
}

function PortalProofPreview({
  proof,
  getReadUrl,
  onOpen,
}: {
  proof: ExecutionProof;
  getReadUrl: (proofId: string) => Promise<string | null>;
  onOpen: () => void;
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!isProofImage(proof)) {
      setPreviewUrl(null);
      return () => {
        cancelled = true;
      };
    }
    void getReadUrl(proof.id).then((url) => {
      if (!cancelled) setPreviewUrl(url);
    });
    return () => {
      cancelled = true;
    };
  }, [proof, getReadUrl]);

  return (
    <button
      type="button"
      onClick={onOpen}
      className="h-16 w-24 overflow-hidden rounded border bg-muted/40 hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary"
      title={proof.caption ?? proof.file_name ?? "Installer proof"}
    >
      {previewUrl ? (
        <img src={previewUrl} alt={proof.caption ?? proof.file_name ?? "Installer proof"} className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <Camera className="h-4 w-4 text-muted-foreground" />
        </div>
      )}
    </button>
  );
}

export function JobSpecExecutionPanel({ jobId, bound }: Props) {
  const { specExecutions, setSpecExecutions, materialKits } = useAppData();
  const kit = useMemo(() => materialKits.find((k) => k.jobId === jobId), [materialKits, jobId]);
  const [installerName, setInstallerName] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(INSTALLER_NAME_STORAGE_KEY) ?? "";
  });
  const [lightboxPhoto, setLightboxPhoto] = useState<{ label: string; url: string } | null>(null);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<Record<string, string>>({});
  const [portalChecklistRows, setPortalChecklistRows] = useState<PortalChecklistRow[]>([]);
  const [portalChecklistLoading, setPortalChecklistLoading] = useState(true);
  const [checklistOverrideEnabled, setChecklistOverrideEnabled] = useState(false);
  const [portalProofGallery, setPortalProofGallery] = useState<{ proofs: ExecutionProof[]; index: number } | null>(null);
  const [portalProofUrl, setPortalProofUrl] = useState<string | null>(null);
  const [portalProofLoading, setPortalProofLoading] = useState(false);
  const [portalProofUploading, setPortalProofUploading] = useState<string | null>(null);
  const [portalProofUrls, setPortalProofUrls] = useState<Map<RequiredPhotoType, string>>(new Map());

  const spec = useMemo(() => resolveBoundSpec(bound), [bound]);
  const customPortalChecklistRows = useMemo(
    () => portalChecklistRows.filter((row) => row.isCustom && row.label.trim()),
    [portalChecklistRows],
  );
  const customPortalChecklistByPhase = useMemo(() => {
    const groups = new Map<JobChecklistPhaseKey, PortalChecklistRow[]>();
    for (const phase of JOB_CHECKLIST_PHASES) groups.set(phase, []);
    for (const row of customPortalChecklistRows) {
      groups.get(row.phaseKey)?.push(row);
    }
    for (const rows of groups.values()) {
      rows.sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.itemIndex - b.itemIndex;
      });
    }
    return groups;
  }, [customPortalChecklistRows]);
  const hasCustomPortalChecklist = customPortalChecklistRows.length > 0;
  const customPortalPhaseCount = useMemo(
    () => Array.from(customPortalChecklistByPhase.values()).filter((rows) => rows.length > 0).length,
    [customPortalChecklistByPhase],
  );
  const portalProofs = useExecutionProofs({ appJobId: jobId, enabled: hasCustomPortalChecklist });
  const portalProofsByCaption = useMemo(() => {
    const map = new Map<string, ExecutionProof[]>();
    for (const proof of portalProofs.proofs) {
      if (proof.superseded_by) continue;
      const key = normalizeProofCaption(proof.caption);
      if (!key) continue;
      const list = map.get(key) ?? [];
      list.push(proof);
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
    return map;
  }, [portalProofs.proofs]);
  const activePortalProof = portalProofGallery?.proofs[portalProofGallery.index] ?? null;

  useEffect(() => {
    let cancelled = false;
    if (!activePortalProof) {
      setPortalProofUrl(null);
      setPortalProofLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setPortalProofLoading(true);
    setPortalProofUrl(null);
    void portalProofs.getReadUrl(activePortalProof.id).then((url) => {
      if (cancelled) return;
      setPortalProofLoading(false);
      if (url) setPortalProofUrl(url);
      else toast.error("Couldn't fetch a signed view URL. Try refreshing.");
    });

    return () => {
      cancelled = true;
    };
  }, [activePortalProof?.id, portalProofs.getReadUrl]);

  const openPortalProofGallery = (proofs: ExecutionProof[], index: number) => {
    setPortalProofGallery({ proofs, index });
  };

  const movePortalProofGallery = (delta: number) => {
    setPortalProofGallery((current) => {
      if (!current || current.proofs.length === 0) return current;
      return { ...current, index: (current.index + delta + current.proofs.length) % current.proofs.length };
    });
  };

  const executionRepo = useMemo(
    () =>
      createExecutionRepository({
        getCachedExecution: (id) => specExecutions.find((e) => e.jobId === id),
        setCachedExecution: (next) =>
          setSpecExecutions((prev) => {
            const idx = prev.findIndex((e) => e.jobId === next.jobId);
            if (idx === -1) return [...prev, next];
            return prev.map((e, i) => (i === idx ? next : e));
          }),
        getMaterialKit: (id) => materialKits.find((k) => k.jobId === id),
      }),
    [specExecutions, setSpecExecutions, materialKits],
  );

  useEffect(() => {
    let cancelled = false;
    const loadRows = async () => {
      setPortalChecklistLoading(true);
      const { data, error } = await supabase
        .from("job_checklist_items")
        .select("id, job_id, phase_key, phase_label, item_index, label, proof_label, requires_proof, sort_order, is_custom, is_active, checked, checked_by, checked_at")
        .eq("job_id", jobId)
        .eq("is_active", true)
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("item_index", { ascending: true });

      if (cancelled) return;
      setPortalChecklistLoading(false);

      if (error) {
        console.warn("[JobSpecExecutionPanel] Failed to load portal checklist rows:", error);
        setPortalChecklistRows([]);
        return;
      }

      const rows = ((data ?? []) as Array<Record<string, unknown>>)
        .map((row): PortalChecklistRow | null => {
          const phase = String(row.phase_key ?? "");
          if (!JOB_CHECKLIST_PHASES.includes(phase as JobChecklistPhaseKey)) return null;
          const phaseKey = phase as JobChecklistPhaseKey;
          const label = String(row.label ?? "").trim();
          const id = String(row.id ?? "").trim();
          if (!id || !label) return null;
          return {
            id,
            jobId: String(row.job_id ?? ""),
            phaseKey,
            phaseLabel: String(row.phase_label ?? "").trim() || DEFAULT_JOB_CHECKLIST_PHASE_LABELS[phaseKey],
            itemIndex: Number.isFinite(Number(row.item_index)) ? Number(row.item_index) : 0,
            label,
            proofLabel: String(row.proof_label ?? "").trim() || null,
            requiresProof: Boolean(row.requires_proof),
            isCustom: row.is_custom === true,
            checked: Boolean(row.checked),
            checkedBy: String(row.checked_by ?? "").trim() || null,
            checkedAt: String(row.checked_at ?? "").trim() || null,
            sortOrder: Number.isFinite(Number(row.sort_order)) ? Number(row.sort_order) : Number(row.item_index) || 0,
          };
        })
        .filter((row): row is PortalChecklistRow => Boolean(row))
        .sort((a, b) => {
          const phaseDelta = (PORTAL_CHECKLIST_PHASE_ORDER[a.phaseKey] ?? 99) - (PORTAL_CHECKLIST_PHASE_ORDER[b.phaseKey] ?? 99);
          if (phaseDelta !== 0) return phaseDelta;
          if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
          return a.itemIndex - b.itemIndex;
        });

      setPortalChecklistRows(rows);
    };

    void loadRows();

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // Lazily ensure an execution record exists once the user lands here.
  const execution: JobSpecExecution | undefined = useMemo(() => {
    if (!bound || !spec) return undefined;
    const existing = specExecutions.find((e) => e.jobId === jobId);
    if (existing) return existing;
    const { execution: created, executions } = ensureExecution(specExecutions, jobId, bound);
    // Defer state set to avoid set-during-render warning.
    queueMicrotask(() => setSpecExecutions(executions));
    return created;
  }, [bound, spec, specExecutions, jobId, setSpecExecutions]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const next = installerName.trim();
    if (!next) {
      window.localStorage.removeItem(INSTALLER_NAME_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(INSTALLER_NAME_STORAGE_KEY, next);
  }, [installerName]);

  useEffect(() => {
    if (!execution) return;
    let cancelled = false;
    const pending: Array<Promise<{ key: string; url: string } | null>> = [];

    for (const phase of execution.phases) {
      for (const photo of phase.photos) {
        if (!photo.attachmentId) continue;
        const key = `${phase.phaseName}:${photo.photoType}:${photo.uploadedAt ?? ""}`;
        if (photoPreviewUrls[key]) continue;
        pending.push(
          getDownloadUrl(photo.attachmentId, 3600)
            .then((url) => ({ key, url }))
            .catch(() => null),
        );
      }
    }

    if (pending.length === 0) return;
    void Promise.all(pending).then((rows) => {
      if (cancelled) return;
      const nextEntries = rows.filter((row): row is { key: string; url: string } => Boolean(row));
      if (nextEntries.length === 0) return;
      setPhotoPreviewUrls((prev) => {
        const merged = { ...prev };
        for (const row of nextEntries) merged[row.key] = row.url;
        return merged;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [execution, photoPreviewUrls]);

  // Fetch portal execution_proofs for this job and build a photoType → signedUrl map.
  // Portal photos are stored in the execution-proofs bucket with bare paths and
  // no phase_key — we infer the photoType by word-overlap matching on caption.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from("execution_proofs")
        .select("id, storage_path, photo_type, caption, kind")
        .eq("execution_id", jobId)
        .is("superseded_by", null)
        .eq("kind", "photo");
      if (error || !data || cancelled) return;

      const rows = data as Array<{ id: string; storage_path: string; photo_type: string | null; caption: string | null; kind: string }>;
      // Resolve each row to a RequiredPhotoType (photo_type column first, caption fallback)
      const resolved = rows
        .map((r) => ({ ...r, resolved: resolvePhotoType(r.photo_type, r.caption) }))
        .filter((r): r is typeof r & { resolved: RequiredPhotoType } => r.resolved !== null && Boolean(r.storage_path));
      if (resolved.length === 0) return;

      const signedEntries = await Promise.all(
        resolved.map(async (r) => {
          const signedUrl = await createSignedStorageThumbnailUrl(r.storage_path, {
            bucket: EXECUTION_PROOFS_BUCKET,
            expiresIn: 3600,
          }).catch(() => "");
          return [r.storage_path, signedUrl] as const;
        }),
      );
      if (cancelled) return;

      const pathToSigned = new Map(signedEntries.filter(([, signedUrl]) => signedUrl));

      const nextMap = new Map<RequiredPhotoType, string>();
      for (const row of resolved) {
        const signedUrl = pathToSigned.get(row.storage_path);
        if (signedUrl && !nextMap.has(row.resolved)) {
          nextMap.set(row.resolved, signedUrl);
        }
      }

      if (!cancelled) setPortalProofUrls(nextMap);
    })();
    return () => { cancelled = true; };
  }, [jobId, spec]);

  const handlePhoto = (phaseName: JobSpecPhaseName, photoType: RequiredPhotoType, file?: File) => {
    if (!file) return;
    executionRepo
      .uploadRequiredPhoto({
        jobId,
        phaseName,
        photoType,
        file,
        actor: installerName.trim() || "installer",
        bound,
      })
      .then(() => toast.success("Photo captured"))
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : "Failed to capture photo");
      });
  };

  const handleCheck = (phaseName: JobSpecPhaseName, itemId: string, checked: boolean) => {
    executionRepo.toggleChecklist({
      jobId,
      phaseName,
      itemId,
      checked,
      actor: installerName.trim() || "installer",
      bound,
    }).catch((error) => {
      toast.error(error instanceof Error ? error.message : "Failed to update checklist");
    });
  };

  const handleSubmitPhase = (phaseName: JobSpecPhaseName) => {
    if (!installerName.trim()) {
      toast.error("Enter your name before submitting a phase.");
      return;
    }
    executionRepo
      .submitPhase({
        jobId,
        phaseName,
        actor: installerName.trim(),
        at: new Date(),
        bound,
      })
      .then(() => toast.success(`Phase "${phaseName}" submitted for QA`))
      .catch((error) => {
        toast.error(error instanceof Error ? error.message : "Failed to submit phase");
      });
  };

  const openPhotoPreview = async (photoLabel: string, photo: { url: string; attachmentId?: string }) => {
    try {
      let previewUrl = photo.url;
      if (photo.attachmentId) {
        previewUrl = await getDownloadUrl(photo.attachmentId, 3600);
      }
      setLightboxPhoto({ label: photoLabel, url: previewUrl });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to open photo preview");
    }
  };

  const handleEnableChecklistOverride = () => {
    const confirmed = window.confirm(
      "Enable checklist override for this job? This lets Koredor App correct checklist status and upload or replace checklist proof photos. Use this only when Ops is intentionally correcting installer execution evidence.",
    );
    if (!confirmed) return;
    setChecklistOverrideEnabled(true);
    toast.warning("Checklist override enabled for this panel.");
  };

  const handlePortalChecklistOverride = async (item: PortalChecklistRow, checked: boolean) => {
    if (!checklistOverrideEnabled) return;
    const patch = checked
      ? { checked: true, checked_at: new Date().toISOString() }
      : { checked: false, checked_at: null };
    const { error } = await supabase
      .from("job_checklist_items")
      .update(patch)
      .eq("id", item.id);

    if (error) {
      toast.error("Could not update checklist override.");
      return;
    }

    setPortalChecklistRows((prev) =>
      prev.map((row) =>
        row.id === item.id
          ? { ...row, checked, checkedAt: checked ? String(patch.checked_at) : null }
          : row,
      ),
    );
    toast.success(checked ? "Checklist item marked checked." : "Checklist item reset.");
  };

  const handlePortalProofUpload = async ({
    file,
    caption,
    supersedesId,
  }: {
    file?: File;
    caption: string;
    supersedesId?: string | null;
  }) => {
    if (!file) return;
    if (!checklistOverrideEnabled) {
      toast.error("Enable checklist override before uploading proof.");
      return;
    }

    // Deduplication — block before anything hits storage or the DB.
    const hash = await computeFileHash(file);
    if (isSessionDuplicate(hash)) {
      toast.warning("Duplicate photo", {
        description: `"${file.name}" was already uploaded in this session.`,
      });
      return;
    }
    const existingDup = findExistingDuplicate(file, portalProofs.proofs);
    if (existingDup && !supersedesId) {
      toast.warning("Duplicate photo", {
        description: `A proof named "${existingDup.file_name}" with the same file size already exists for this job.`,
      });
      return;
    }

    const uploadKey = getUploadKey(caption, supersedesId);
    setPortalProofUploading(uploadKey);
    try {
      const mimeType = file.type || "application/octet-stream";
      const res = await requestExecutionProofUploadUrl({
        executionId: jobId,
        fileName: file.name,
        mimeType,
        byteSize: file.size,
        kind: getProofUploadKind(file),
        caption,
        capturedAt: new Date().toISOString(),
        supersedesId: supersedesId ?? undefined,
      });

      if (!res.ok) {
        const err = res as { ok: false; error: string; detail?: string };
        throw new Error(err.detail || `Upload URL request failed: ${err.error}`);
      }

      await uploadProofBytes(res.uploadUrl, res.token, mimeType, file);
      registerUploadHash(hash);
      toast.success(supersedesId ? "Proof photo replaced." : "Proof photo uploaded.");
      portalProofs.refresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not upload proof photo.");
    } finally {
      setPortalProofUploading(null);
    }
  };

  if (!bound || !spec) {
    return (
      <Card className="shadow-none border-dashed">
        <CardContent className="p-4 text-sm text-muted-foreground">
          No JobSpec bound to this job. Installer execution requires a bound spec.
        </CardContent>
      </Card>
    );
  }

  if (!execution) {
    return (
      <Card className="shadow-none">
        <CardContent className="p-4 text-sm text-muted-foreground">Initializing execution...</CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-none">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-[0.12em]">
              Installer Execution
            </p>
            <h3 className="text-base font-semibold leading-tight mt-0.5">
              {spec.label}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              <code className="font-mono">{formatSpecRef(bound)}</code>
            </p>
          </div>
          <div className="w-48">
            <Input
              value={installerName}
              onChange={(e) => setInstallerName(e.target.value)}
              placeholder="Installer name"
              className="h-8 text-xs"
            />
          </div>
        </div>

        {portalChecklistLoading ? (
          <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            Loading installer portal checklist rows...
          </div>
        ) : hasCustomPortalChecklist ? (
          <div className="space-y-3">
            <div className="rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs text-muted-foreground">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span>
                  Showing live installer portal checklist rows from <code className="font-mono">job_checklist_items</code>.
                  Koredor App only updates checks or proof photos when checklist override is enabled.
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant={checklistOverrideEnabled ? "default" : "outline"}
                  className="h-7 text-[11px]"
                  onClick={checklistOverrideEnabled ? () => setChecklistOverrideEnabled(false) : handleEnableChecklistOverride}
                >
                  <ShieldAlert className="mr-1 h-3 w-3" />
                  {checklistOverrideEnabled ? "Exit override" : "Checklist override"}
                </Button>
              </div>
            </div>

            {JOB_CHECKLIST_PHASES.map((phaseKey) => {
              const rows = customPortalChecklistByPhase.get(phaseKey) ?? [];
              if (rows.length === 0) return null;
              const checkedCount = rows.filter((row) => row.checked).length;
              const phaseLabel = getPortalPhaseLabel(phaseKey, rows);
              const requiredProofs = rows.filter((row) => row.requiresProof);
              const requiredProofCaptions = new Set(requiredProofs.map((row) => normalizeProofCaption(proofCaptionForItem(row))).filter(Boolean));
              const additionalProofs = portalProofs.proofs
                .filter((proof) => {
                  if (proof.superseded_by) return false;
                  const caption = normalizeProofCaption(proof.caption);
                  if (!caption || requiredProofCaptions.has(caption)) return false;
                  if (captionMatchesPhase(caption, phaseLabel)) return true;
                  if (customPortalPhaseCount === 1 && (caption === "additional" || caption.startsWith("additional "))) return true;
                  return false;
                })
                .sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at));
              return (
                <div key={phaseKey} className="rounded-md border p-3 space-y-3 bg-background">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">
                        {phaseLabel}
                      </span>
                      <Badge variant="outline">Portal Checklist</Badge>
                    </div>
                    <div className="text-[11px] text-muted-foreground tabular-nums">
                      {checkedCount}/{rows.length} checks
                    </div>
                  </div>

                  {requiredProofs.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                        Required Photos
                      </div>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {requiredProofs.map((item) => {
                          const proofCaption = proofCaptionForItem(item);
                          const proofs = portalProofsByCaption.get(normalizeProofCaption(proofCaption)) ?? [];
                          const latestProof = proofs[0];
                          const uploadKey = getUploadKey(proofCaption, latestProof?.id);
                          return (
                          <div key={`${item.id}-proof`} className={`rounded-md border px-2 py-2 text-xs ${proofs.length > 0 ? "border-success/40 bg-success/5" : "text-muted-foreground"}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate font-medium text-foreground">{proofCaption}</p>
                                <p className="text-[10px] text-muted-foreground">
                                  {proofs.length > 0 ? `${proofs.length} uploaded` : "Awaiting proof upload"}
                                </p>
                              </div>
                              {proofs.length > 0 ? (
                                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                              ) : (
                                <Camera className="h-4 w-4 shrink-0 text-muted-foreground" />
                              )}
                            </div>
                            {proofs.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {proofs.map((proof, proofIndex) => (
                                  <PortalProofPreview
                                    key={proof.id}
                                    proof={proof}
                                    getReadUrl={portalProofs.getReadUrl}
                                    onOpen={() => openPortalProofGallery(proofs, proofIndex)}
                                  />
                                ))}
                              </div>
                            )}
                            {checklistOverrideEnabled && (
                              <label className="mt-2 inline-flex h-7 cursor-pointer items-center rounded-md border px-2 text-[11px] font-medium text-foreground hover:bg-muted/50">
                                {portalProofUploading === uploadKey
                                  ? "Uploading..."
                                  : latestProof
                                    ? "Replace latest"
                                    : "Upload proof"}
                                <input
                                  type="file"
                                  accept="image/*,application/pdf"
                                  className="hidden"
                                  disabled={Boolean(portalProofUploading)}
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    event.currentTarget.value = "";
                                    void handlePortalProofUpload({
                                      file,
                                      caption: proofCaption,
                                      supersedesId: latestProof?.id,
                                    });
                                  }}
                                />
                              </label>
                            )}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Optional Additional Photos
                    </div>
                    {checklistOverrideEnabled && (
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          const additionalCaption = `Additional (${phaseLabel})`;
                          const latestAdditionalProof = additionalProofs[0];
                          const uploadKey = getUploadKey(additionalCaption, null);
                          const replaceKey = getUploadKey(additionalCaption, latestAdditionalProof?.id);
                          return (
                            <>
                              <label className="inline-flex h-7 cursor-pointer items-center rounded-md border px-2 text-[11px] font-medium hover:bg-muted/50">
                                {portalProofUploading === uploadKey ? "Uploading..." : "Upload additional"}
                                <input
                                  type="file"
                                  accept="image/*,application/pdf"
                                  className="hidden"
                                  disabled={Boolean(portalProofUploading)}
                                  onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    event.currentTarget.value = "";
                                    void handlePortalProofUpload({
                                      file,
                                      caption: additionalCaption,
                                    });
                                  }}
                                />
                              </label>
                              {latestAdditionalProof && (
                                <label className="inline-flex h-7 cursor-pointer items-center rounded-md border px-2 text-[11px] font-medium hover:bg-muted/50">
                                  {portalProofUploading === replaceKey ? "Uploading..." : "Replace latest"}
                                  <input
                                    type="file"
                                    accept="image/*,application/pdf"
                                    className="hidden"
                                    disabled={Boolean(portalProofUploading)}
                                    onChange={(event) => {
                                      const file = event.target.files?.[0];
                                      event.currentTarget.value = "";
                                      void handlePortalProofUpload({
                                        file,
                                        caption: additionalCaption,
                                        supersedesId: latestAdditionalProof.id,
                                      });
                                    }}
                                  />
                                </label>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                    {additionalProofs.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {additionalProofs.map((proof, proofIndex) => (
                          <PortalProofPreview
                            key={proof.id}
                            proof={proof}
                            getReadUrl={portalProofs.getReadUrl}
                            onOpen={() => openPortalProofGallery(additionalProofs, proofIndex)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed px-2 py-1.5 text-xs text-muted-foreground">
                        No additional phase photos uploaded yet.
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      Checklist
                    </div>
                    <div className="space-y-1">
                      {rows.map((item) => (
                        <div key={item.id} className="flex items-center gap-2 text-xs">
                          <Checkbox
                            checked={item.checked}
                            disabled={!checklistOverrideEnabled}
                            onCheckedChange={(value) => {
                              void handlePortalChecklistOverride(item, value === true);
                            }}
                          />
                          <span className={item.checked ? "line-through text-muted-foreground" : ""}>
                            {item.label}
                          </span>
                          {item.checkedAt && (
                            <span className="ml-auto text-[10px] text-muted-foreground">
                              Checked {new Date(item.checkedAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
        <div className="space-y-3">
          {spec.phases.map((specPhase) => {
            const phase = execution.phases.find((p) => p.phaseName === specPhase.name)!;
            const unlocked = isPhaseUnlocked(execution, spec, specPhase.name, kit);
            const checksDone = phase.checklist.filter((c) => c.checked).length;
            const checksTotal = specPhase.checklistItems.length;
            const photosDone = specPhase.requiredPhotos.filter((pt) =>
              phase.photos.some((ph) => ph.photoType === pt) || portalProofUrls.has(pt),
            ).length;
            const photosTotal = specPhase.requiredPhotos.length;
            const ready = (checksTotal === 0 || checksDone === checksTotal) && specPhase.requiredPhotos.every((pt) =>
              phase.photos.some((p) => p.photoType === pt) || portalProofUrls.has(pt),
            );
            const totalRequirements = checksTotal + photosTotal;
            const pct = totalRequirements > 0 ? Math.round(((checksDone + photosDone) / totalRequirements) * 100) : 0;
            const locked = !unlocked || phase.status === "qa_passed" || phase.status === "submitted";

            return (
              <div
                key={specPhase.name}
                className={`rounded-md border p-3 space-y-3 ${
                  unlocked ? "bg-background" : "bg-muted/30 opacity-60"
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    {!unlocked && <Lock className="h-3.5 w-3.5 text-muted-foreground" />}
                    <span className="text-sm font-semibold">{specPhase.label}</span>
                    {statusBadge(phase.status)}
                  </div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">
                    {pct}% · {photosDone}/{photosTotal} photos ·{" "}
                    {checksDone}/{checksTotal} checks
                  </div>
                </div>

                {/* QA Failure callout */}
                {phase.status === "qa_failed" && phase.qaFailReason && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs">
                    <div className="flex items-center gap-1.5 font-semibold text-destructive">
                      <RotateCcw className="h-3 w-3" /> Returned by QA
                    </div>
                    <p className="text-foreground/80 mt-0.5">{phase.qaFailReason}</p>
                  </div>
                )}

                {/* Photos */}
                <div className="space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Required Photos
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {specPhase.requiredPhotos.map((pt) => {
                      const capturedPhoto = phase.photos.find((ph) => ph.photoType === pt);
                      const portalUrl = portalProofUrls.get(pt);
                      const captured = Boolean(capturedPhoto) || Boolean(portalUrl);
                      const previewKey = `${specPhase.name}:${pt}:${capturedPhoto?.uploadedAt ?? ""}`;
                      const resolvedPreviewUrl =
                        (capturedPhoto?.attachmentId ? photoPreviewUrls[previewKey] : undefined) ??
                        capturedPhoto?.url ??
                        portalUrl;
                      const hasPreview = Boolean(resolvedPreviewUrl);
                      return (
                        <div
                          key={pt}
                          className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-left text-xs transition-colors ${
                            captured
                              ? "border-success/40 bg-success/5"
                              : "border-border hover:bg-muted/50"
                          } ${locked ? "opacity-50" : ""}`}
                        >
                          <label className={`flex min-w-0 flex-1 items-center gap-2 ${locked ? "cursor-not-allowed" : "cursor-pointer"}`}>
                            <input
                              type="file"
                              accept="image/*"
                              disabled={locked}
                              className="hidden"
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                if (file) handlePhoto(specPhase.name, pt, file);
                                event.currentTarget.value = "";
                              }}
                            />
                            {captured ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                            ) : (
                              <Camera className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            )}
                            <span className="truncate">{formatPhotoLabel(pt)}</span>
                          </label>
                          {hasPreview && resolvedPreviewUrl ? (
                            <button
                              type="button"
                              onClick={() =>
                                setLightboxPhoto({
                                  label: formatPhotoLabel(pt),
                                  url: resolvedPreviewUrl,
                                })
                              }
                              className="inline-flex h-10 w-16 shrink-0 items-center justify-center overflow-hidden rounded border border-border/60 bg-black/30"
                              title="Open uploaded photo"
                            >
                              <img
                                src={resolvedPreviewUrl}
                                alt={formatPhotoLabel(pt)}
                                className="h-full w-full object-cover"
                              />
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Checklist */}
                <div className="space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Checklist
                  </div>
                  <div className="space-y-1">
                    {specPhase.checklistItems.map((item) => {
                        const c = phase.checklist.find((x) => x.itemId === item.id);
                        const checked = !!c?.checked;
                        return (
                          <label
                            key={item.id}
                            className={`flex items-center gap-2 text-xs ${
                              locked ? "cursor-not-allowed" : "cursor-pointer"
                            }`}
                          >
                            <Checkbox
                              checked={checked}
                              disabled={locked}
                              onCheckedChange={(v) =>
                                handleCheck(specPhase.name, item.id, v === true)
                              }
                            />
                            <span className={checked ? "line-through text-muted-foreground" : ""}>
                              {item.label}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>

                {/* Submit */}
                {phase.status !== "qa_passed" && phase.status !== "submitted" && (
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-[10px] text-muted-foreground">
                      {!unlocked
                        ? specPhase.name === "Pan, Substrate & Wall Installation"
                          ? "Locked — confirm Material Kit (or log PM override) before install."
                          : "Locked — prior phases must pass QA first."
                        : !ready
                        ? "Complete all photos and checks to submit."
                        : "Ready to submit for QA."}
                    </p>
                    <Button
                      size="sm"
                      disabled={!unlocked || !ready}
                      onClick={() => handleSubmitPhase(specPhase.name)}
                    >
                      Submit Phase
                    </Button>
                  </div>
                )}

                {/* Submitted timestamp */}
                {phase.completedAt && phase.status !== "qa_failed" && (
                  <p className="text-[10px] text-muted-foreground">
                    Submitted {new Date(phase.completedAt).toLocaleString()}
                    {phase.completedBy ? ` by ${phase.completedBy}` : ""}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        )}
      </CardContent>
      <Dialog open={lightboxPhoto !== null} onOpenChange={(open) => !open && setLightboxPhoto(null)}>
        <DialogContent className="w-[96vw] max-w-[96vw] h-[92vh] max-h-[92vh] p-0 overflow-hidden bg-black border-border/60">
          {lightboxPhoto ? (
            <div className="flex h-full flex-col">
              <div className="px-3 py-2 border-b border-white/10 text-xs text-white/80">
                {lightboxPhoto.label}
              </div>
              <div className="flex-1 flex items-center justify-center p-2 overflow-auto">
                <img
                  src={lightboxPhoto.url}
                  alt={lightboxPhoto.label}
                  className="max-h-[86vh] max-w-[94vw] w-auto h-auto object-contain rounded"
                />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <Dialog open={portalProofGallery !== null} onOpenChange={(open) => !open && setPortalProofGallery(null)}>
        <DialogContent className="w-[96vw] max-w-[96vw] h-[92vh] max-h-[92vh] p-0 overflow-hidden bg-black border-border/60">
          {activePortalProof ? (
            <div className="flex h-full flex-col">
              <div className="flex items-center justify-between gap-3 border-b border-white/10 px-3 py-2 text-xs text-white/80">
                <span className="truncate">{activePortalProof.caption ?? activePortalProof.file_name ?? "Installer proof"}</span>
                {(portalProofGallery?.proofs.length ?? 0) > 1 && (
                  <span className="shrink-0">
                    {(portalProofGallery?.index ?? 0) + 1} / {portalProofGallery?.proofs.length ?? 0}
                  </span>
                )}
              </div>
              <div className="relative flex flex-1 items-center justify-center overflow-auto p-2">
                {portalProofLoading && !portalProofUrl ? (
                  <div className="text-sm text-white/70">Loading proof...</div>
                ) : portalProofUrl && isProofImage(activePortalProof) ? (
                  <img
                    src={portalProofUrl}
                    alt={activePortalProof.caption ?? activePortalProof.file_name ?? "Installer proof"}
                    className="max-h-[86vh] max-w-[94vw] h-auto w-auto rounded object-contain"
                  />
                ) : portalProofUrl ? (
                  <iframe
                    src={portalProofUrl}
                    title={activePortalProof.caption ?? activePortalProof.file_name ?? "Installer proof"}
                    className="h-[86vh] w-[94vw] rounded bg-background"
                  />
                ) : (
                  <div className="text-sm text-white/70">Couldn't load this proof.</div>
                )}
                {(portalProofGallery?.proofs.length ?? 0) > 1 && (
                  <>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="absolute left-4 top-1/2 h-9 w-9 -translate-y-1/2 bg-background/90"
                      onClick={() => movePortalProofGallery(-1)}
                    >
                      <span className="sr-only">Previous proof</span>
                      {"<"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="absolute right-4 top-1/2 h-9 w-9 -translate-y-1/2 bg-background/90"
                      onClick={() => movePortalProofGallery(1)}
                    >
                      <span className="sr-only">Next proof</span>
                      {">"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default JobSpecExecutionPanel;
